<template>
    <div>
        <b-card
            :id="'unit' + unit.id"
            class="unit mb-2"
            :class="{
                'dark': dark,
                'deleting': unit.deleting,
            }"
            :header-bg-variant="'secondary'"
            :header-text-variant="dark ? 'white' : null"
            :border-variant="dark ? 'secondary' : null"
            :header-class="unit.online_only? 'online-only-bg' : null"
            no-body
        >
            <template #header>
                <CardID :id="unit.id" type="Week" variant="light" />
                <div class="d-flex align-items-center">
                    <div class="d-flex flex-grow-1 flex-nowrap align-items-center mr-3">
                        <span
                            v-if="workflow !== 'standards'"
                            class="collapse-icon mr-2"
                            :class="unit.visible ? null : 'collapsed'"
                            :aria-expanded="unit.visible ? 'true' : 'false'"
                            @click="toggleVisibility('visible')"
                        >
                            <span class="open">
                                <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                            </span>
                            <span class="closed">
                                <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                            </span>
                        </span>
                        <div class="d-flex align-items-center flex-grow-1">
                            <div class="mr-2">
                                <select
                                    :value="unit.order_number"
                                    class="form-control form-control-sm custom-select custom-select-sm text-dark"
                                    @change="reorderUnits(unit, $event)"
                                    :disabled="!can_update_week || disabled"
                                >
                                    <option v-for="(on, index) in potential_week_order_numbers" :key="index" :value="on.order_num">
                                       <span v-if="units_are_lessons">Lesson</span> <span v-else>Week</span> {{on.order_num}}
                                    </option>
                                </select>
                                <b-badge
                                v-if="multipleWeekNumbers(unit.order_number)"
                                :class="{
                                    'badge-warning text-white mt-2': publication.is_trunk == 1,
                                    'badge-danger mt-2': publication.is_trunk != 1,
                                }"
                                >
                                    Multiple Week {{unit.order_number}}s
                                </b-badge>
                            </div>

                            <div class="flex-grow-1 mr-3">
                                <Title
                                    v-model="unit.title"
                                    :unit="unit"
                                    :variant="'secondary'"
                                    :special_issue="publication.id == publication_id && all_unit_groups.length > 1"
                                    @update="updateUnitTitle"
                                    :disabled="!can_update_week || disabled"
                                />
                            </div>

                            <div class="d-flex flex-column mr-3">
                                <div>
                                    <b-form-checkbox
                                        v-model="se_locked"
                                        :checked="se_locked"
                                        :disabled="publication_se_locked || !can_update_week || disabled"
                                        switch
                                        @change="toggleStudentEditionLock"
                                    >
                                        Lock SE
                                    </b-form-checkbox>
                                </div>
                                <div>
                                    <b-form-checkbox
                                        v-model="is_sample"
                                        :checked="is_sample"
                                        switch
                                        @change="toggleSampleWeek"
                                        :disabled="!can_update_week || disabled"
                                    >
                                        Sample Week
                                    </b-form-checkbox>
                                </div>
                                <div>
                                    <b-form-checkbox
                                        v-model="online_only"
                                        :checked="online_only"
                                        switch
                                        @change="toggleOnlineOnly"
                                        :disabled="!can_update_week || disabled"
                                    >
                                        Online Only
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="ml-auto">
                        <span v-if="unit.deleting">
                            <font-awesome-icon :icon="['fad', 'circle-notch']" spin />
                        </span>
                        <div v-else class="d-flex flex-column">
                            <b-btn
                                :variant="copy_to_clipboard_btn_variant"
                                :class="{'active': copying_to_clipboard}"
                                content="Copy Week Link"
                                v-tippy="{placement: 'top'}"
                                @click="copyToClipboard(unit.id)"
                                :disabled="copying_to_clipboard"
                                class="my-2"
                                size="sm"
                            >
                                <span v-if="copying_to_clipboard">
                                    <font-awesome-icon :icon="['fas', 'check']" size="sm" fixed-width />
                                </span>
                                <span v-else>
                                    <font-awesome-icon :icon="['fas', 'link']" size="sm" fixed-width />
                                </span>
                            </b-btn>
                            <b-dropdown size="sm" variant="light" dropleft>
                                <template #button-content>
                                    <font-awesome-icon :icon="['fad', 'cog']" />
                                </template>

                                <b-dropdown-item @click="show_teacher_edition_modal = true">
                                    <font-awesome-icon :icon="['fad', 'book']" class="text-primary" fixed-width />
                                    Teacher Editions
                                </b-dropdown-item>
                                <b-dropdown-item @click="exportArticles(unit)">
                                    <font-awesome-icon :icon="['fad', 'file-alt']" class="text-secondary" fixed-width />
                                    Download articles
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="setUnitArticlesAsComplete(unit)"
                                    :disabled="!can_edit_article || disabled"
                                >
                                    <font-awesome-icon :icon="['fas', 'check']" class="text-info" fixed-width />
                                    Mark Articles AS Complete
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="setUnitArticlesAsPrintable(unit)"
                                    :disabled="!can_edit_article || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'print']" class="text-secondary" fixed-width />
                                    Mark Articles AS Printable
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="setUnitArticlesAsNotPrintable(unit)"
                                    :disabled="!can_edit_article || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'computer-classic']" class="text-secondary" fixed-width />
                                    Mark Articles AS Not Printable
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="convertLinkedArticlesToDuplicates"
                                    :disabled="!can_convert_linked_articles || converting_linked_articles"
                                >
                                    <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width />
                                    <span v-if="converting_linked_articles">Converting Linked Articles...</span>
                                    <span v-else>Convert Linked Articles to Duplicates</span>
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="moveUnit(unit, unit_group)"
                                    :disabled="!can_move_week || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'level-up']" class="text-secondary" fixed-width />
                                    Move
                                </b-dropdown-item>

                                <!-- added back into trunk 4/16/2024 -->
                                <b-dropdown-item
                                    @click="startUnitDuplication(unit, unit_group)"
                                    :disabled="!can_duplicate_week"
                                >
                                    <font-awesome-icon :icon="['fad', 'clone']" class="text-info" fixed-width />
                                    Duplicate
                                </b-dropdown-item>


                                <b-dropdown-item
                                    @click="ImportWeek"
                                    :disabled="!can_import_week || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'share']" class="text-info" fixed-width />
                                    Import 3.0
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click="deleteUnit(unit, unit_group)"
                                    :disabled="!can_delete_week || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width />
                                    Delete
                                </b-dropdown-item>
                                <template v-if="can_request_report">
                                    <b-dropdown-divider />
                                    <b-dropdown-header>Reports</b-dropdown-header>
                                    <b-dropdown-item @click="$emit('request-report', {
                                        reportType: 'articles_with_narration',
                                        weekId: unit.id,
                                        label: `${units_are_lessons ? 'Lesson' : 'Week'} ${unit.order_number}: ${unit.title || 'No title'}`,
                                    })">
                                        <font-awesome-icon :icon="['fad', 'file-alt']" class="text-primary" fixed-width />
                                        Articles With Narration
                                    </b-dropdown-item>
                                    <b-dropdown-item @click="$emit('request-report', {
                                        reportType: 'articles_without_narration',
                                        weekId: unit.id,
                                        label: `${units_are_lessons ? 'Lesson' : 'Week'} ${unit.order_number}: ${unit.title || 'No title'}`,
                                    })">
                                        <font-awesome-icon :icon="['fad', 'file-alt']" class="text-primary" fixed-width />
                                        Articles Without Narration
                                    </b-dropdown-item>
                                    <b-dropdown-item @click="$emit('view-report-requests', {
                                        type: 'week',
                                        id: unit.id,
                                        label: `${units_are_lessons ? 'Lesson' : 'Week'} ${unit.order_number}: ${unit.title || 'No title'}`,
                                    })">
                                        <font-awesome-icon :icon="['fad', 'list-alt']" class="text-secondary" fixed-width />
                                        View Reports
                                    </b-dropdown-item>
                                </template>
                            </b-dropdown>
                        </div>
                    </div>
                </div>
                <div v-if="unit && unit.trunks && unit.trunks.length > 0 && publication.is_trunk != 1 && unit.trunks[0].is_trunk == 1" class="row offset-md alert alert-info mt-2 pt-2">
                    <div class="col">
                        <div>Trunk SKU: {{ unit.trunks[0].sku }}</div>
                        <div>Week Title: {{ unit.trunks[0].week_title }}</div>
                        <div>Week Id: {{ unit.trunks[0].week_id }}</div>
                    </div>
                    <div class="col">
                        <button @click="compareToTrunk(unit)" :class="unit.trunk_differences ? 'btn btn-danger btn-sm float-right' : 'btn btn-primary btn-sm float-right'">View Trunk Differences</button>
                    </div>
                </div>
            </template>

            <b-card-body>
                <div v-if="isElementVisible(element_visibility, 'unit_week_standards')" :id="'unit' + unit.id + 'standards'">
                    <div class="h6">
                        Week Standards
                        (<span
                            v-if="unit.standards"
                            :class="{'text-success': unit.standards.length, 'text-danger': !unit.standards.length}"
                        >{{filtered_unit_standards.length}}</span>)
                    </div>

                    <standards-multiselect
                        v-model="filtered_unit_standards"
                        :standards="available_unit_standards"
                        :state_grade="state_grade"
                        @addStandard="addStandard"
                        @removeStandard="removeStandard"
                        :disabled="disabled"
                    />
                </div>
            </b-card-body>

            <b-collapse v-model="unit.visible" :class="unit.visible ? null : 'collapsed'">
                <div v-if="unit.visible && workflow !== 'standards'">
                    <b-card-body v-if="unit.loading" class="border-top rounded-0">
                        <content-placeholders>
                            <content-placeholders-text :lines="3" />
                        </content-placeholders>
                    </b-card-body>
                    <div v-else>
                        <b-list-group-item v-if="unit.branches && publication.is_trunk == 1 && isElementVisible(element_visibility, 'unit_trunk_pedigree_week')">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit_trunk_pedigree_week_visible ? null : 'collapsed'"
                                        :aria-expanded="unit_trunk_pedigree_week_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('trunk_pedigree_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        Trunk Pedigree
                                    </span>
                                </div>

                                <b-collapse v-model="unit_trunk_pedigree_week_visible" :class="unit_trunk_pedigree_week_visible ? null : 'collapsed'">
                                    <div :id="'unit' + unit.id + 'trunk_pedigree'">
                                    <table v-if="unit.branches.length > 0" class="table table-striped">
                                        <thead>
                                            <tr>
                                                <th class="col"> SKU </th>
                                                <th class="col"> Week </th>
                                                <th class="col"> Title </th>
                                                <th class="col"> Week Id </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="branch in unit.branches">
                                                <td> {{ branch.sku }} </td>
                                                <td> {{ branch.week_number }} </td>
                                                <td> {{ branch.week_title }} </td>
                                                <td> {{ branch.week_id }} </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-else class="text-center"> Week is not yet assigned in any branch</div>
                                </div>
                                </b-collapse>

                            </b-list-group-item>
                        <b-list-group>
                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_panels')" class="panel-section-container">
                                <div class="h6">Panels</div>
                                <panel-section
                                    :id="'unit' + unit.id + 'panel'"
                                    :panels="unit_data.panels"
                                    :unit="unit"
                                    :publication="publication"
                                    :disabled="disabled"
                                />
                            </b-list-group-item>


                            <b-list-group-item v-if="publication.subject != 'science' && publication.subject != 'health_wellness' && can_view_deprecated_fields" :id="'unit' + unit.id + 'essential_questions'">
                                <div class="h6">Essential Questions</div>

                                <TipTap
                                    v-model="unit_data.essential_questions"
                                    :menu_type="wysiwyg_menu_type"
                                    @input="setContent('essential_questions', $event)"
                                    @blur="updateUnitByProperty('essential_questions', unit_data.essential_questions)"
                                    :readonly="disabled"
                                />
                            </b-list-group-item>

                            <b-list-group-item v-if="publication.subject != 'science' && publication.subject != 'health_wellness' && can_view_deprecated_fields" :id="'unit' + unit.id + 'curriculum_notes'">
                                <div class="h6">Curriculum Notes</div>

                                <TipTap
                                    v-model="unit_data.curriculum_notes"
                                    :menu_type="wysiwyg_menu_type"
                                    @input="setContent('curriculum_notes', $event)"
                                    @blur="updateUnitByProperty('curriculum_notes', unit_data.curriculum_notes)"
                                    :readonly="disabled"
                                />
                            </b-list-group-item>

                            <b-list-group-item v-if="publication.subject != 'science' && publication.subject != 'health_wellness' && can_view_deprecated_fields" :id="'unit' + unit.id + 'historical_thinking_skills'">
                                <div class="h6">Historical Thinking Skills</div>

                                <multiselect
                                    v-model="unit_data.historical_thinking_skills"
                                    :options="historical_thinking_skills"
                                    label="historical_thinking_skill"
                                    track-by="historical_thinking_skill"
                                    @input="updateUnitByProperty('historical_thinking_skills', unit_data.historical_thinking_skills)"
                                    :close-on-select="false"
                                    multiple
                                    :disabled="!can_update_week || disabled"
                                />
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_crossword')" :id="'unit' + unit.id + 'crossword'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.crossword_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.crossword_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('crossword_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span>Crossword</span>
                                    </span>
                                </div>

                                <b-collapse v-model="unit.crossword_visible" :class="unit.crossword_visible ? null : 'collapsed'">
                                    <div v-if="unit.crossword_visible" class="mt-2">
                                        <div v-if="unit.crossword_loading">
                                            <content-placeholders>
                                                <content-placeholders-text :lines="3" />
                                            </content-placeholders>
                                        </div>
                                        <div v-else>
                                            <crossword-old
                                                ref="crossword"
                                                :publication_id="publication.id"
                                                :unit_id="unit.id"
                                                :unit="unit"
                                                :disabled="disabled"
                                            />
                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_weekly_vocabulary')" :id="'unit' + unit.id + 'vocabulary'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.vocabulary_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.vocabulary_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('vocabulary_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span v-if="units_are_lessons">Lesson</span>
                                        <span v-else>Weekly</span>
                                        Vocabulary
                                    </span>
                                </div>

                                <b-collapse v-model="unit.vocabulary_visible">
                                        <vocabulary-list
                                            v-if="unit.vocabulary_visible"
                                            class="mt-2"
                                            :unit="unit"
                                            :disabled="!can_update_week || disabled"
                                        />
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="can_view_deprecated_fields" :id="'unit' + unit.id + 'teacher-notes'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit_teacher_notes_visible ? null : 'collapsed'"
                                        :aria-expanded="unit_teacher_notes_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('teacher_notes_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        Teacher Notes
                                    </span>
                                </div>

                                <b-collapse v-model="unit_teacher_notes_visible" :class="unit_teacher_notes_visible ? null : 'collapsed'">
                                    <ul class="my-2">
                                        <li v-if="publication.subject != 'science' && publication.subject != 'health_wellness'">
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'sequencing_of_articles', offset: -144}">
                                                Sequencing of Articles
                                            </a>
                                        </li>
                                        <li v-if="publication.subject != 'science' && publication.subject != 'health_wellness'">
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'think_deeply', offset: -144}">
                                                Think Deeply
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'well_being_questions', offset: -144}">
                                                Well-Being Questions
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'lets_write', offset: -144}">
                                                Let's Write
                                            </a>
                                        </li>
                                        <li v-if="publication.state_grades.length && publication.state_grades.length > 0 && publication.state_grades[0].state == 'TX'">
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'teks_vocabulary', offset: -144}">
                                                TEKS Vocabulary
                                            </a>
                                        </li>
                                    </ul>

                                    <b-card
                                        v-if="publication.subject != 'science' && publication.subject != 'health_wellness'"
                                        :id="'unit' + unit.id + 'sequencing_of_articles'"
                                        class="my-2"
                                    >
                                        <template #header>
                                            Sequencing of Articles
                                        </template>

                                        <TipTap
                                            v-model="unit_data.sequencing_of_articles"
                                            :menu_type="wysiwyg_menu_type"
                                            :menu_items="['bold', 'italic', 'underline', 'bullet_list']"
                                            @input="setContent('sequencing_of_articles', $event)"
                                            @blur="updateUnitByProperty('sequencing_of_articles', unit_data.sequencing_of_articles)"
                                            :readonly="!can_update_week || disabled"
                                        />

                                        <!-- The sequencing of the articles this week was designed to both scaffold and build upon each other -->
                                    </b-card>

                                    <b-card
                                        v-if="publication.subject != 'science' && publication.subject != 'health_wellness'"
                                        :id="'unit' + unit.id + 'think_deeply'"
                                        class="my-2"
                                    >
                                        <template #header>
                                            Think Deeply
                                        </template>

                                        <TipTap
                                            v-model="unit_data.think_deeply"
                                            :menu_type="wysiwyg_menu_type"
                                            :menu_items="['bold', 'italic', 'underline', 'bullet_list', 'ordered_list', 'highlight']"
                                            @input="setContent('think_deeply', $event)"
                                            @blur="updateUnitByProperty('think_deeply', unit_data.think_deeply)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'well_being_questions'" class="my-2">
                                        <template #header>
                                            Well-Being Questions
                                        </template>

                                        <TipTap
                                            v-model="unit_data.well_being_questions"
                                            :menu_type="wysiwyg_menu_type"
                                            :menu_items="['bold', 'italic', 'underline', 'bullet_list']"
                                            @input="setContent('well_being_questions', $event)"
                                            @blur="updateUnitByProperty('well_being_questions', unit_data.well_being_questions)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'lets_write'" class="my-2">
                                        <template #header>
                                            Let's Write
                                        </template>

                                        <TipTap
                                            v-model="unit_data.lets_write"
                                            :menu_type="wysiwyg_menu_type"
                                            :menu_items="['bold', 'italic', 'underline', 'bullet_list']"
                                            @input="setContent('lets_write', $event)"
                                            @blur="updateUnitByProperty('lets_write', unit_data.lets_write)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card
                                        :id="'unit' + unit.id + 'teks_vocabulary'"
                                        class="mt-2 mb-0"
                                        v-if="publication.state_grades.length && publication.state_grades.length > 0 && publication.state_grades[0].state == 'TX'"
                                    >
                                        <template #header>
                                            TEKS Vocabulary
                                        </template>
                                        <TipTap
                                            v-model="unit_data.teks_vocabulary"
                                            :menu_type="wysiwyg_menu_type"
                                            :menu_items="['bold', 'italic', 'underline', 'bullet_list']"
                                            @input="setContent('teks_vocabulary', $event)"
                                            @blur="updateUnitByProperty('teks_vocabulary', unit_data.teks_vocabulary)"
                                            :readonly="!can_update_week"
                                        />
                                    </b-card>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="can_view_deprecated_fields" :id="'unit' + unit.id + 'integrated-standards'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit_integrated_standards_visible ? null : 'collapsed'"
                                        :aria-expanded="unit_integrated_standards_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('integrated_standards_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        Integrated Standards
                                    </span>
                                </div>

                                <b-collapse v-model="unit_integrated_standards_visible" :class="unit_integrated_standards_visible ? null : 'collapsed'">
                                    <ul class="my-2">
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_ela', offset: -144}">
                                                English Language Arts
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_math', offset: -144}">
                                                Mathematics
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_science', offset: -144}">
                                                Science
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_fine_arts', offset: -144}">
                                                Fine Arts
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_pe', offset: -144}">
                                                Physical Education
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_health', offset: -144}">
                                                Health & Wellness
                                            </a>
                                        </li>
                                        <li>
                                            <a href="#" v-scroll-to="{el: '#unit' + unit.id + 'standards_technology', offset: -144}">
                                                Technology
                                            </a>
                                        </li>
                                    </ul>

                                    <b-card :id="'unit' + unit.id + 'standards_ela'" class="mt-2">
                                        <template #header>
                                            English Language Arts
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_ela"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_ela', $event)"
                                            @blur="updateUnitByProperty('standards_ela', unit_data.standards_ela)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_math'" class="mt-2">
                                        <template #header>
                                            Mathematics
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_math"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_math', $event)"
                                            @blur="updateUnitByProperty('standards_math', unit_data.standards_math)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_science'" class="mt-2">
                                        <template #header>
                                            Science
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_science"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_science', $event)"
                                            @blur="updateUnitByProperty('standards_science', unit_data.standards_science)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_fine_arts'" class="mt-2">
                                        <template #header>
                                            Fine Arts
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_fine_arts"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_fine_arts', $event)"
                                            @blur="updateUnitByProperty('standards_fine_arts', unit_data.standards_fine_arts)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_pe'" class="mt-2">
                                        <template #header>
                                            Physical Education
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_pe"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_pe', $event)"
                                            @blur="updateUnitByProperty('standards_pe', unit_data.standards_pe)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_health'" class="mt-2">
                                        <template #header>
                                            Health & Wellness
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_health"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_health', $event)"
                                            @blur="updateUnitByProperty('standards_health', unit_data.standards_health)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                    <b-card :id="'unit' + unit.id + 'standards_technology'" class="mt-2">
                                        <template #header>
                                            Technology
                                        </template>

                                        <TipTap
                                            v-model="unit_data.standards_technology"
                                            :menu_type="wysiwyg_menu_type"
                                            @input="setContent('standards_technology', $event)"
                                            @blur="updateUnitByProperty('standards_technology', unit_data.standards_technology)"
                                            :readonly="!can_update_week || disabled"
                                        />
                                    </b-card>

                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="can_view_deprecated_fields" :id="'unit' + unit.id + 'designer-report'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit_designer_report_visible ? null : 'collapsed'"
                                        :aria-expanded="unit_designer_report_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('designer_report_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        Designer Report
                                    </span>
                                </div>

                                <b-collapse v-model="unit_designer_report_visible" :class="unit_designer_report_visible ? null : 'collapsed'">
                                    <div v-if="unit_designer_report_visible" class="designer-report mt-2">
                                        <div v-if="unit_designer_report_loading">
                                            <content-placeholders>
                                                <content-placeholders-text :lines="3" />
                                            </content-placeholders>
                                        </div>
                                        <div v-else>
                                            <div class="row">
                                                <div class="col-lg order-lg-1 order-2">
                                                    <div v-if="!unit_articles.length" class="text-left text-danger">
                                                        There are no articles in this week.
                                                    </div>
                                                    <div
                                                        v-for="page in designer_report"
                                                        :key="'page' + page.id"
                                                        :id="'page' + page.id"
                                                        class="page-slot"
                                                        :class="{'mt-2': page.id > 1, 'd-none': !page.articles.length}"
                                                    >
                                                        <h5
                                                            class="mb-0"
                                                            :class="{
                                                                'text-muted': !page.articles.length,
                                                                'extended-reading': page.page == 'Extended Reading'
                                                            }"
                                                        >
                                                            <span v-if="page.page !== 'Extended Reading'">Page </span>{{page.page}}
                                                        </h5>

                                                        <div v-if="page.articles.length" class="border-left pl-3">
                                                            <b-card
                                                                :id="'page' + page.id + 'article' + article.id"
                                                                v-for="article in page.articles"
                                                                :key="'article' + article.id"
                                                                class="article my-2"
                                                                no-body
                                                            >
                                                                <template #header>
                                                                    <div class="d-flex align-items-center">
                                                                        <div>
                                                                            <span v-if="article.title">{{article.title}}</span>
                                                                            <span v-else-if="article.notes">Note</span>
                                                                            <span v-else-if="article.placeholder_type">Placeholder</span>
                                                                            <span v-else class="text-muted">no title</span>
                                                                        </div>
                                                                        <div v-if="article.title" class="ml-auto">
                                                                            <copy-to-clipboard-button :value="article.title" :asHtml="false" :size="'sm'"/>
                                                                        </div>
                                                                    </div>
                                                                </template>
                                                                <b-card-body>
                                                                    <div v-if="article_body">
                                                                        <div class="d-flex align-items-center">
                                                                            <div v-html="article_body" class="v-html">
                                                                            </div>
                                                                            <div v-if="articleWordCount(article_body) > 0" class="ml-3">
                                                                                <copy-to-clipboard-button :value="article_body" :asHtml="true" :size="'sm'"/>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div v-else-if="article.notes" v-html="article.notes" class="v-html"></div>
                                                                    <div v-else-if="article.placeholder_type">
                                                                        {{article.placeholder_type}}
                                                                    </div>
                                                                    <div v-else class="text-danger">
                                                                        no article body
                                                                    </div>
                                                                </b-card-body>
                                                            </b-card>
                                                        </div>
                                                        <div v-else class="text-muted pt-1">
                                                            -
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-lg order-lg-2 order-1 mb-lg-0 mb-3">
                                                    <div
                                                        v-sticky="{zIndex: 11, stickyTop: 144}"
                                                        class="py-lg-3 bg-white"
                                                        style="max-height: calc(100vh - 145px); overflow: scroll;"
                                                        v-if="unit_articles.length"
                                                    >
                                                        <div v-for="page in designer_report"  :key="'page' + page.id" :class="{'d-none': !page.articles.length}">
                                                            <div v-if="page.articles.length">
                                                                <a href="#" v-scroll-to="{el: '#page' + page.id, offset: -144}">
                                                                    {{page.page == 'Extended Reading' ? 'Extended Reading' : 'Page ' + page.page}}
                                                                </a>
                                                            </div>
                                                            <div v-else class="text-muted">
                                                                {{page.page == 'Extended Reading' ? 'Extended Reading' : 'Page ' + page.page}}
                                                            </div>

                                                            <div v-if="page.articles.length">
                                                                <ul class="mb-0">
                                                                    <li v-for="article in page.articles" :key="'article' + article.id">
                                                                        <a href="#" v-scroll-to="{el: '#page' + page.id + 'article' + article.id, offset: -144}">
                                                                            <span v-if="article.title">{{article.title}}</span>
                                                                            <span v-else-if="article.notes">Note</span>
                                                                            <span v-else-if="article.placeholder_type">Placeholder</span>
                                                                            <span v-else class="text-muted">no title</span>

                                                                            <span v-if="article_body" class="text-success ml-3">
                                                                                <font-awesome-icon :icon="['fad', 'pencil']" />
                                                                            </span>
                                                                        </a>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                            <div v-else class="text-muted pl-3">
                                                                -
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_weekly_assessment_questions')" :id="'unit' + unit.id + 'assessment'">
                                <div class="h6 m-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.assessment_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.assessment_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('assessment_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>

                                        <span v-if="units_are_lessons">Lesson</span><span v-else>Weekly</span> Assessment
                                    </span>
                                </div>

                                <b-collapse v-model="unit.assessment_visible" :class="unit.assessment_visible ? null : 'collapsed'">
                                    <div :id="'unit' + unit.id + 'assessment-questions'">
                                        <div v-if="unit.assessment_visible">
                                            <div class="questions">
                                                <div v-if="unit.questions_loading">
                                                    <content-placeholders>
                                                        <content-placeholders-text :lines="3" />
                                                    </content-placeholders>
                                                </div>
                                                <div v-else>
                                                    <div v-sticky="{zIndex: 11, stickyTop: 144}" class="d-flex align-items-center bg-white py-3">
                                                        <div class="mr-3">
                                                            Questions
                                                        </div>
                                                        <div class="ml-auto">
                                                            <span class="mr-3">
                                                                <a :href="'/questionList/unit/'+unit.id" target="_blank" class="pull-right" ><font-awesome-icon :icon="['fad', 'clipboard']" style="color: grey;" fixed-width /></a>
                                                            </span>
                                                            <b-dropdown size="sm" variant="outline-primary" right>
                                                                <template #button-content>
                                                                    <font-awesome-icon :icon="['fad', 'cog']" />
                                                                </template>
                                                                <b-dropdown-item
                                                                    @click="newQuestion"
                                                                    :disabled="!can_add_question || disabled"
                                                                >
                                                                    <font-awesome-icon :icon="['fad', 'plus-square']" class="text-primary" fixed-width /> Create question
                                                                </b-dropdown-item>
                                                            </b-dropdown>
                                                        </div>
                                                    </div>

                                                    <div v-show="unit.questions && unit.questions.length">
                                                        <div v-for="(question, index) in unit.questions" :key="index">
                                                            <Question
                                                                :publication_id="publication_id"
                                                                :unit_group="unit_group"
                                                                :unit="unit"
                                                                :question="question"
                                                                :questions="unit.questions"
                                                                :state_grade="state_grade"
                                                                :available_standards="filtered_unit_standards"
                                                                @update="getQuestions"
                                                                :disabled="disabled"
                                                            />
                                                        </div>
                                                    </div>

                                                    <b-alert variant="danger" class="mb-2" :show="!unit.questions.length || unit.questions && !unit.questions.length">
                                                        0 Weekly Questions
                                                    </b-alert>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item
                                v-if="isElementVisible(element_visibility, 'unit_weekly_printables')"
                                :id="'unit_group' + unit_group.id + 'unit' + unit.id + 'graphic-organizers'"
                                class="graphic-organizer"
                            >
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.worksheets_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.worksheets_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('worksheets_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span v-if="units_are_lessons">Lesson</span><span v-else>Weekly</span> Printables
                                    </span>
                                </div>

                                <b-collapse
                                    v-model="unit.worksheets_visible"
                                    :class="unit.worksheets_visible ? null : 'collapsed'"
                                >
                                    <WorksheetsList
                                        :unit="unit"
                                        :unit_group="unit_group"
                                        :publication="publication"
                                        :disabled="disabled"
                                    />
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_weekly_teacher_edition')" :id="'unit' + unit.id + 'teacher-edition'">
                                <div class="h6 m-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.teacher_edition_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.teacher_edition_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('teacher_edition_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span v-if="units_are_lessons">Lesson</span><span v-else>Weekly</span> Teacher Edition
                                    </span>
                                </div>

                                <b-collapse v-model="unit.teacher_edition_visible" :class="unit.teacher_edition_visible ? null : 'collapsed'">
                                    <div :id="'unit' + unit.id + 'teacher-edition'">
                                        <div v-if="unit.teacher_edition_visible">
                                            <div v-if="teacher_edition_data" class="row">
                                                <div class="col-6" v-if="unit.lesson_plan_url">
                                                    <div class="mt-2 mb-2">
                                                        <strong>
                                                            Weekly Teacher Edition: {{ unit.lesson_plan_url }}
                                                        </strong>
                                                        <div>
                                                            <a :href="`${CDN_URL}${unit.lesson_plan_url}`" target="_blank" class="item-url">
                                                                {{unit.lesson_plan_title}} - SWO Link (will not immediately update)
                                                            </a>
                                                        </div>
                                                        <div>
                                                            <a :href="`${AWS_URL}${unit.lesson_plan_url}`" target="_blank" class="item-url">
                                                                {{unit.lesson_plan_title}} - SBP Link
                                                            </a>
                                                        </div>

                                                    </div>

                                                    <div>
                                                        <button
                                                            class="btn btn-danger"
                                                            @click="deleteUnitTe"
                                                            :disabled="!can_update_teacher_edition || disabled"
                                                        >
                                                            Delete TE
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <b-dropdown
                                                class="mt-3"
                                                size="sm"
                                                variant="outline-primary"
                                                dropup
                                                right
                                                :disabled="!can_update_teacher_edition || disabled"
                                            >
                                                <template #button-content>
                                                    <font-awesome-icon :icon="['fad', 'plus-square']" />
                                                    <span v-if="unit.lesson_plan_url">
                                                        Replace Week Level Teacher Edition
                                                    </span>
                                                    <span v-else>
                                                        Upload Week Level Teacher Edition
                                                    </span>
                                                </template>
                                                <b-dropdown-item @click.prevent="openUploadTeacherEdition">
                                                    <font-awesome-icon :icon="['fad', 'plus-square']" class="text-muted" fixed-width />
                                                        Upload PDF From Desktop
                                                </b-dropdown-item>
                                            </b-dropdown>

                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_flipbooks')" :id="'unit' + unit.id + 'flipbooks'">
                                <div class="h6 mb-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.flipbooks_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.flipbooks_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('flipbooks_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span>Flip Books</span>
                                    </span>
                                </div>

                                <b-collapse
                                    v-model="unit.flipbooks_visible"
                                    :class="unit.flipbooks_visible ? null : 'collapsed'"
                                >
                                    <FlipBookList
                                        v-model="unit.flipbooks_visible"
                                        :unit="unit"
                                        :disabled="disabled"
                                    />
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_weekly_assessment_pdf')" :id="'unit' + unit.id + 'weekly_assessment_pdf'">
                                <div class="h6">
                                    <span v-if="units_are_lessons">Lesson</span>
                                    <span v-else>Weekly</span>
                                    Assessment PDF
                                </div>
                                <div v-if="uploading_assessment">
                                    <b-spinner small label="Loading..."></b-spinner>
                                </div>
                                <div v-else>
                                    <div v-if="unit_data.assessment_url">
                                        <b-list-group>
                                            <b-list-group-item>
                                                <div class="d-flex align-items-center">
                                                    <div class="d-flex flex-column">
                                                        <b-link
                                                            :href="`${CDN_URL}${unit_data.assessment_url}`"
                                                            target="_blank"
                                                            class="text-primary"
                                                        >
                                                            {{unit_data.assessment_title}} - SWO Link (will not immediately update)
                                                        </b-link>
                                                        <b-link
                                                            :href="`${AWS_URL}${unit_data.assessment_url}`"
                                                            target="_blank"
                                                            class="text-primary"
                                                        >
                                                            {{unit_data.assessment_title}} - SBP Link
                                                        </b-link>
                                                    </div>
                                                    <div v-if="!disabled" class="ml-auto actions">
                                                        <b-link
                                                            href="#"
                                                            class="text-danger"
                                                            @click.prevent="removeWeeklyAssessment"
                                                            v-if="can_update_week"
                                                        >
                                                            <font-awesome-icon :icon="['fad', 'trash']" />
                                                        </b-link>
                                                    </div>
                                                </div>
                                            </b-list-group-item>
                                        </b-list-group>
                                    </div>
                                    <div v-else>
                                        <b-form-file
                                            v-model="assessment_file"
                                            accept="application/pdf"
                                            :disabled="!can_update_week || disabled"
                                        />
                                        <b-form-text>
                                            <span class="text-danger">No file has been uploaded</span>
                                        </b-form-text>
                                    </div>
                                </div>
                            </b-list-group-item>

                            <b-list-group-item v-if="publication.state_grades.length && publication.state_grades.length > 0 && publication.state_grades[0].state == 'CA' && isElementVisible(element_visibility, 'unit_educator_guide_for_ca')" :id="'unit' + unit.id + 'ca_educator_guide'">
                                <div class="h6">Educator Guide for Lesson</div>

                                <TipTap
                                    v-model="unit_data.ca_educator_guide"
                                    :menu_type="wysiwyg_menu_type"
                                    @input="setContent('ca_educator_guide', $event)"
                                    @blur="updateUnitByProperty('ca_educator_guide', unit_data.ca_educator_guide)"
                                />
                            </b-list-group-item>

                            <b-list-group-item v-if="publication.state_grades.length && publication.state_grades.length > 0 && publication.state_grades[0].state == 'CA' && isElementVisible(element_visibility, 'unit_eld_standards_ca')" :id="'unit' + unit.id + 'ca_eld_standards'">
                                <div class="h6">ELD Standards</div>

                                <TipTap
                                    v-model="unit_data.ca_eld_standards"
                                    :menu_type="wysiwyg_menu_type"
                                    @input="setContent('ca_eld_standards', $event)"
                                    @blur="updateUnitByProperty('ca_eld_standards', unit_data.ca_eld_standards)"
                                />
                            </b-list-group-item>

                            <b-list-group-item
                                v-if="isElementVisible(element_visibility, 'unit_student_edition')" :id="'unit' + unit.id + 'student_edition'"
                            >
                                <StudentEdition
                                    :unit="unit"
                                    :unit_data="unit_data"
                                    :disabled="disabled"
                                    :units_are_lessons="units_are_lessons"
                                />
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_bibliography')" :id="'unit' + unit.id + 'bibliography'">
                                <div class="h6 m-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.bibliography_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.bibliography_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('bibliography_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span>Bibliography</span>
                                    </span>
                                </div>
                                <b-collapse v-model="unit.bibliography_visible" :class="unit.bibliography_visible ? null : 'collapsed'">
                                    <div class="mt-3" :id="'unit' + unit.id + 'bibliography'">
                                        <div v-if="bibliography && bibliography.length > 0">
                                            <ul>
                                                <li v-for="(citation, citation_index) in bibliography">         
                                                    <div class="row">
                                                        <div class="col-2">page: {{ citation.page }}</div>
                                                        <div class="col-4">Article Title: {{ citation.article_title }} </div>
                                                        <div class="col-6">Citation(s): 
                                                            <TipTap
                                                                v-model="citation.citations"
                                                                :readonly="true"
                                                            />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>            
                                        </div>
                                        <div v-else>
                                            <div class="alert alert-danger">Articles contain no citations</div>
                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_linked_articles')" :id="'unit' + unit.id + 'linked-articles'">
                                <div class="h6 m-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.linked_articles_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.linked_articles_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('linked_articles_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>
                                        <span>Linked Articles</span>
                                        <b-badge v-if="linkedArticles.length" variant="warning" pill class="ml-2">
                                            {{ linkedArticles.length }}
                                        </b-badge>
                                    </span>
                                </div>

                                <b-collapse
                                    v-model="unit.linked_articles_visible"
                                    :class="unit.linked_articles_visible ? null : 'collapsed'"
                                >
                                    <div v-if="unit.linked_articles_visible" class="mt-3">
                                        <div v-if="linked_articles_loading">
                                            <content-placeholders>
                                                <content-placeholders-text :lines="3" />
                                            </content-placeholders>
                                        </div>

                                        <div v-else-if="linkedArticles.length">
                                            <div class="alert alert-warning">
                                                <span>
                                                    {{ linkedArticles.length }} linked article<span v-if="linkedArticles.length !== 1">s</span>
                                                    assigned to this week.
                                                </span>
                                            </div>

                                            <b-list-group>
                                                <b-list-group-item
                                                    v-for="article in linkedArticles"
                                                    :key="article.unit_page_slot_id"
                                                    class="d-flex align-items-center justify-content-between"
                                                >
                                                    <div>
                                                        <div class="font-weight-bold">
                                                            {{ article.title || 'no title' }}
                                                        </div>
                                                        <div class="text-muted small">
                                                            Article ID: {{ article.article_id }} | Page: {{ article.page }}
                                                        </div>
                                                    </div>

                                                    <b-badge variant="warning" pill>
                                                        Linked Article
                                                    </b-badge>
                                                </b-list-group-item>
                                            </b-list-group>
                                        </div>

                                        <div v-else class="alert alert-success mb-0">
                                            No linked articles assigned to this week.
                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>

                            <b-list-group-item v-if="isElementVisible(element_visibility, 'unit_articles')" :id="'unit' + unit.id + 'articles'">
                                <div class="h6 m-0">
                                    <span
                                        class="collapse-icon text-dark mr-1"
                                        :class="unit.articles_visible ? null : 'collapsed'"
                                        :aria-expanded="unit.articles_visible ? 'true' : 'false'"
                                        @click="toggleVisibility('articles_visible')"
                                    >
                                        <span class="open">
                                            <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                                        </span>
                                        <span class="closed">
                                            <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                                        </span>

                                        Articles
                                        <div style="margin-left: 75%;">
                                            <b-form-checkbox
                                                v-model="custom_article_numbering"
                                                :checked="custom_article_numbering"
                                                :disabled="publication_se_locked || disabled"
                                                switch
                                                @change="toggleCustomNumbering"
                                            >
                                                Custom Article Numbering
                                            </b-form-checkbox>
                                        </div>

                                    </span>
                                </div>

                                <b-collapse
                                    v-model="unit.articles_visible"
                                    :class="unit.articles_visible ? null : 'collapsed'"
                                >
                                    <div v-if="unit.articles_visible" class="mt-2">
                                        <div v-if="unit.articles_loading">
                                            <content-placeholders>
                                                <content-placeholders-text :lines="3" />
                                            </content-placeholders>
                                        </div>

                                        <div v-else>
                                            <Layout
                                                :publication_id="publication_id"
                                                :publication="publication"
                                                :unit_group="unit_group"
                                                :unit="unit"
                                                :state_grade="state_grade"
                                                :disabled="disabled"
                                                :available_standards="filtered_unit_standards"
                                            />
                                        </div>
                                    </div>
                                </b-collapse>
                            </b-list-group-item>
                        </b-list-group>
                    </div>
                </div>
            </b-collapse>
        </b-card>

        <move-unit-modal
            v-model="show_move_unit_modal"
            :publication_id="publication_id"
            :unit_group="unit_group"
            :unit="unit"
            @hide="show_move_unit_modal = false"
        />

        <duplicate-unit-modal
            v-model="show_duplicate_unit_modal"
            @hide="show_duplicate_unit_modal = false"
            :unit="unit"
        />

        <question-modal
            v-model="show_question_modal"
            :unit_group="unit_group"
            :unit="unit"
            :questions="unit.questions"
            :state_grade="state_grade"
            :available_standards="filtered_unit_standards"
            @create="createQuestion"
            @update="getQuestions"
            @hide="show_question_modal = false"
        />

        <teacher-edition-modal
            v-model="show_teacher_edition_modal"
            :publication="publication"
            :unit="unit"
            @hide="show_teacher_edition_modal = false"
        />

        <upload-teacher-edition-modal
            v-model="show_upload_teacher_edition_modal"
            :unit="unit"
            :publication="publication"
            :unit_group="unit_group"
            @hide="show_upload_teacher_edition_modal = false"
            @update_te="updateTe"
        />

        <trunk-comparison-modal
            v-model="show_trunk_comparison_modal"
            :comparison="trunk_comparison"
            :unit="unit"
            @hide="show_trunk_comparison_modal = false"
            @update_to_trunk="updateToTrunk"
            @refreshUnit="refreshUnit"
        />

        <panel-validation-failed-modal
            v-model="show_panel_validation_failed_modal"
            :untitled_modals="untitled_modals"
            @hide="show_panel_validation_failed_modal = false"
        />

    </div>
</template>

<style lang="scss">
@import '@/assets/css/_colors';

.unit {
    > .card-header {
        .custom-control-input[disabled] ~ .custom-control-label,
        .custom-control-input:disabled ~ .custom-control-label {
            color: lighten($secondary, 30%);
        }
    }
}

.designer-report {
    .article {
        img {
            max-width: 100%;
        }
    }
}

.panel-section-container {
    border-radius: unset !important;
}
</style>

<style lang="scss" scoped>
@import '@/assets/css/_colors';

.card {
    .card-header {
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
    }

    &:not(.dark) {
        border-color: rgba(0, 0, 0, .125);
    }

    &.dark {
        > .card-header {
            .form-control {
                color: $white;
            }

            .form-control::placeholder { color: darken($white, 10%); }
            :-ms-input-placeholder.form-control { color: darken($white, 10%); }
            .form-control::-ms-input-placeholder { color: darken($white, 10%); }
        }
    }
}

.extended-reading {
    color: $purple;
}

.online-only-bg {
    background-color: $purple !important;
}
</style>

<script>
import VueSticky from 'vue-sticky'
import { mapState } from 'vuex'
import { isElementVisible } from '@/common/elementVisibility'
import unit_layout from '@/common/unit_layout'
import colors from '@/common/colors'
import CardID from '@/components/CardID'
import Title from '@/components/Title'
import CrosswordOld from '@/components/unit/CrosswordOld'
import Layout from '@/components/unit/layout/Index'
import Question from '@/components/unit/Question'
import FlipBookList from '@/components/flipbooks/FlipBookList'
import CopyToClipboardButton from '@/components/CopyToClipboardButton'
import TipTap from '@/components/tiptap/TipTap'
import StandardsMultiselect from '@/components/standards/StandardsMultiselect'
import MoveUnitModal from '@/components/modals/unit/MoveUnitModal'
import DuplicateUnitModal from '@/components/modals/unit/DuplicateUnitModal'
import TeacherEditionModal from '@/components/modals/unit/TeacherEditionModal'
import QuestionModal from '@/components/modals/questions/QuestionModal'
import PanelSection from '@/components/panels/PanelSection'
import UploadTeacherEditionModal from '@/components/modals/unit_group/UploadTeacherEditionModal'
import eventBus from '@/common/event-bus'
import copy from 'copy-html-to-clipboard'
import TrunkComparisonModal from '@/components/modals/unit/TrunkComparisonModal'
import PanelValidationFailedModal from '@/components/modals/publication/PanelValidationFailedModal'
import draggable from 'vuedraggable'
import { AWS_URL, CDN_URL, convertCdnUrlToAWS } from '@/common/urlHelpers'
import VocabularyList from '@/components/unit/VocabularyList'
import WorksheetsList from '@/components/worksheet/WorksheetsList'
import StudentEdition from '@/components/unit/StudentEdition'

export default {
    data: () => ({
        articles: [],
        filtered_unit_standards: [],
        filtered_unit_group_standards: [],

        historical_thinking_skills: [
            {
                historical_thinking_skill: 'Chronological Thinking'
            },
            {
                historical_thinking_skill: 'Analytical Thinking'
            },
            {
                historical_thinking_skill: 'Critical Thinking'
            },
            {
                historical_thinking_skill: 'Strategic Thinking'
            }
        ],

        unit_layout: unit_layout,
        designer_report: unit_layout,
        colors: colors,

        unit_data: {},
        unit_cache: {},

        crossword: {},
        assessment_file: null,
        uploading_assessment: false,
        uploading_student_edition: false,
        vocabulary_visible: false,
        show_move_unit_modal: false,
        show_duplicate_unit_modal: false,
        show_teacher_edition_modal: false,
        show_question_modal: false,

        potential_week_order_numbers: [
            { order_num: 1  }, { order_num: 2  }, { order_num: 3  }, { order_num: 4  }, { order_num: 5  }, { order_num: 6  }, { order_num: 7  }, { order_num: 8  }, { order_num: 9  }, { order_num: 10 },
            { order_num: 11 }, { order_num: 12 }, { order_num: 13 }, { order_num: 14 }, { order_num: 15 }, { order_num: 16 }, { order_num: 17 }, { order_num: 18 }, { order_num: 19 }, { order_num: 20 },
            { order_num: 21 }, { order_num: 22 }, { order_num: 23 }, { order_num: 24 }, { order_num: 25 }, { order_num: 26 }, { order_num: 27 }, { order_num: 28 }, { order_num: 29 }, { order_num: 30 },
            { order_num: 31 }, { order_num: 32 }, { order_num: 33 }, { order_num: 34 }, { order_num: 35 }, { order_num: 36 }, { order_num: 37 }, { order_num: 38 }, { order_num: 39 }, { order_num: 40 },
            { order_num: 41 }, { order_num: 42 }, { order_num: 43 }, { order_num: 44 }, { order_num: 45 }, { order_num: 46 }, { order_num: 47 }, { order_num: 48 }, { order_num: 49 }, { order_num: 50 },
            { order_num: 51 }, { order_num: 52 }, { order_num: 53 }, { order_num: 54 }, { order_num: 55 }, { order_num: 56 }, { order_num: 57 }, { order_num: 58 }, { order_num: 59 }, { order_num: 60 },
            { order_num: 61 }, { order_num: 62 }, { order_num: 63 }, { order_num: 64 }, { order_num: 65 }, { order_num: 66 }, { order_num: 67 }, { order_num: 68 }, { order_num: 69 }, { order_num: 70 },
            { order_num: 71 }, { order_num: 72 }, { order_num: 73 }, { order_num: 74 }, { order_num: 75 }, { order_num: 76 }, { order_num: 77 }, { order_num: 78 }, { order_num: 79 }, { order_num: 80 },
            { order_num: 81 }, { order_num: 82 }, { order_num: 83 }, { order_num: 84 }, { order_num: 85 }, { order_num: 86 }, { order_num: 87 }, { order_num: 88 }, { order_num: 89 }, { order_num: 90 },
            { order_num: 91 }, { order_num: 92 }, { order_num: 93 }, { order_num: 94 }, { order_num: 95 }, { order_num: 96 }, { order_num: 97 }, { order_num: 98 }, { order_num: 99 }, { order_num: 100 },
        ],
        teacher_edition_visible: false,
        show_upload_teacher_edition_modal: false,
        teacher_edition_data: null,
        trunk_comparison: null,
        show_trunk_comparison_modal: false,
        show_panel_validation_failed_modal: false,
        untitled_modals: [],
        copying_to_clipboard: false,
        available_unit_standards: [],
        bibliography: [],
        bibliography_visable: false,
        linked_articles_loading: false,
        converting_linked_articles: false,
    }),
    props: [
        'publication_id', //the 'current' or actual pub - with linked pubs, the parent and child use the child pub id which is this
        'publication', //can be the parent or child pub with linked pubs
        'unit_groups',
        'unit_group',
        'unit',
        'dark',
        'state_grade',
        'units_are_lessons',
        'disabled',
    ],
    components: {
        WorksheetsList,
        VocabularyList,
        CardID,
        Title,
        CrosswordOld,
        Layout,
        Question,
        CopyToClipboardButton,
        TipTap,
        StandardsMultiselect,
        MoveUnitModal,
        DuplicateUnitModal,
        QuestionModal,
        TeacherEditionModal,
        PanelSection,
        UploadTeacherEditionModal,
        draggable,
        TrunkComparisonModal,
        PanelValidationFailedModal,
        FlipBookList,
        StudentEdition,
    },
    directives: {
        'sticky': VueSticky,
    },
    computed: {
        ...mapState({
            workflow: state => state.utilities.workflow,
            all_unit_groups: state => state.unit_groups.unit_groups,
            wysiwyg_menu_type: state => state.settings.wysiwyg_menu_type,
            element_visibility: state => state.settings.element_visibility,
            user: state => state.user.user
        }),
        copy_to_clipboard_btn_variant () {
            let variant = "light"

            if (this.copying_to_clipboard) {
                variant = 'success'
            }

            return variant
        },
        publication_grades () {
            let publication_grades = []

            _.forEach(this.publication.state_grades, row => {
                publication_grades.push(row.grade)
            })

            publication_grades = _.uniq(publication_grades)
            return publication_grades
        },
        standards () {
            let standards = []

            if (this.unit_data.standards) {
                standards = this.unit_data.standards
            } else if (this.unit.standards) {
                standards = this.unit.standards
            }

            return standards
        },
        is_activity: {
            get () {
                let is_activity = null

                if (this.unit.is_activity) {
                is_activity = true
                } else {
                    is_activity = false
                }

                return is_activity
            },
            set () {

            }
        },
        se_locked: {
            get () {
                let se_locked = null

                if (this.unit.se_locked) {
                    se_locked = true
                } else {
                    se_locked = false
                }

                return se_locked
            },
            set () {

            }
        },
        is_sample: {
            get () {
                let is_sample = null

                if (this.unit.is_sample) {
                    is_sample = true
                } else {
                    is_sample = false
                }

                return is_sample
            },
            set () {

            }
        },
        custom_article_numbering: {
            get () {
                let custom_article_numbering = null

                if (this.unit.custom_article_numbering) {
                    custom_article_numbering = true
                } else {
                    custom_article_numbering = false
                }

                return custom_article_numbering
            },
            set () {

            }
        },
        online_only: {
            get () {
                let online_only = null

                if (this.unit.online_only) {
                    online_only = true
                } else {
                    online_only = false
                }

                return online_only
            },
            set () {

            }
        },
        publication_se_locked () {
            let se_locked

            if (this.publication.se_locked) {
                se_locked = true
            } else {
                se_locked = false
            }

            return se_locked
        },
        all_units() { //need units across all groups for reordering
            let units = []
            _.each(this.unit_groups, unit_group => {
                _.each(unit_group.units, unit => {
                    units.push(unit)
                })
            })
            return _.orderBy(units, 'order_number')
        },
        can_edit_article(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article')
        },
        can_move_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'move_week')
        },
        can_duplicate_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_week')
        },
        can_import_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'import_week')
        },
        can_create_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_week')
        },
        can_update_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_week')
        },
        can_delete_week(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_week')
        },
        can_request_report() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'request_report')
        },
        can_update_teacher_edition(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_teacher_edition')
        },
        can_add_question(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'add_question')
        },
        AWS_URL() {
            return AWS_URL;
        },
        CDN_URL() {
            return CDN_URL;
        },
        can_view_deprecated_fields() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'view_deprecated_fields');
        },
        linkedArticles() {
            if (!Array.isArray(this.unit.articles)) {
                return []
            }

            return this.unit.articles.filter((article) => {
                if (article.placeholder_type) {
                    return false
                }

                return this.hasLinkedArticlePedigrees(article)
            })
        },
        can_convert_linked_articles() {
            const hasDuplicatePermission = this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_article')
            const hasUnlinkPermission = this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_article')

            return hasDuplicatePermission && hasUnlinkPermission && !this.disabled
        },
    },
    watch: {
        'assessment_file': {
            handler (file) {
                if (file) {
                    this.uploadWeeklyAssessment(file)
                }
            },
        },
        'unit.visible': {
            handler (visible) {
                if (visible) {
                    this.getUnit()
                }
            },
            immediate: true
        },
        'unit.teacher_edition_visible': {
            handler (visible) {
                if (visible) {
                    this.getTeacherEditionData()
                }
            },
            immediate: true
        },
        'unit.bibliography_visible': {
            handler (visible) {
                if (visible) {
                    this.getBibliographyData()
                }
            },
            immediate: true
        },
        'unit.linked_articles_visible': {
            async handler (visible) {
                if (visible) {
                    this.linked_articles_loading = true

                    try {
                        await this.getArticles()
                    } catch (error) {
                        if (
                            error &&
                            error.response &&
                            error.response.status &&
                            error.response.status == 401
                        ) {
                            this.$set(this.unit, 'linked_articles_visible', false)
                        }
                    } finally {
                        this.linked_articles_loading = false
                    }
                } else {
                    if (!this.unit.articles_visible) {
                        this.$set(this.unit, 'articles', null)
                    }

                    this.linked_articles_loading = false
                }
            },
            immediate: true
        },
        'unit.assessment_visible': {
            handler (visible) {
                if (visible) {
                    this.$set(this.unit, 'questions_loading', true)

                    this.getQuestions()
                    .then(() => {
                        this.$set(this.unit, 'questions_loading', false)
                    })
                    .catch(error => {
                        if (error) {
                            if (
                                error.response &&
                                error.response.status &&
                                error.response.status == 401
                            ) {
                                this.$set(this.unit, 'questions_loading', false)
                                this.$set(this.unit, 'assessment_visible', false)
                            }
                        }
                    })
                }
            }
        },
        'unit_designer_report_visible': {
            handler (visible) {
                if (visible) {
                    this.$set(this.unit, 'designer_report_loading', true)
                    this.designer_report = _.cloneDeep(this.unit_layout)

                    this.getArticles()
                    .then(response => {
                        this.$set(this.unit, 'designer_report_loading', false)

                        let articles = []
                        articles = response.data

                        _.forEach(articles, article => {
                            _.forEach(this.designer_report, page => {
                                if (page.page == article.page && !_.find(page.articles, article)) {
                                    page.articles.push(article)
                                }
                            })
                        })
                    })
                    .catch(error => {
                        if (error) {
                            if (
                                error.response &&
                                error.response.status &&
                                error.response.status == 401
                            ) {
                                this.$set(this.unit, 'designer_report_visible', false)
                            }
                        }
                    })
                }
            }
        },
        'unit.articles_visible': {
            handler (visible) {
                if (visible) {
                    this.$set(this.unit, 'articles_loading', true)

                    this.getArticles()
                    .then(() => {
                        this.$set(this.unit, 'articles_loading', false)
                    })
                    .catch(error => {
                        if (error) {
                            if (
                                error.response &&
                                error.response.status &&
                                error.response.status == 401
                            ) {
                                this.$set(this.unit, 'articles_visible', false)
                            }

                            this.$set(this.unit, 'articles_loading', false)
                        }
                    })
                } else {
                    if (!this.unit.linked_articles_visible) {
                        this.$set(this.unit, 'articles', null)
                    }

                    this.$set(this.unit, 'articles_loading', false)
                }
            }
        },
        state_grade(state_grade) {
            this.filterUnitGroupStandards(state_grade)
            this.filterUnitStandards(state_grade)
        }
    },
    methods: {
        convertCdnUrlToAWS, // method imported from urlHelpers
        isElementVisible,
        multipleWeekNumbers(order_number){
            if(this.units_are_lessons){
                return false;
            }

            let times_assigned = 0;
            _.forEach(this.unit_groups, ug => {
                _.forEach(ug.units, un => {
                    if(un.order_number == order_number){
                        times_assigned++;
                    }
                })
            })
            if(times_assigned > 1){
                return true;
            }
            else{
                return false;
            }
        },
        async ImportWeek() {
            try {
                const validationResponse = await this.axios.get('/api/units/validate_import/' + this.unit.id)

                if (!validationResponse.data.valid) {
                    this.untitled_modals = validationResponse.data.untitled_panels || []
                    this.show_panel_validation_failed_modal = (
                        this.untitled_modals.length > 0 
                    )
                    return
                }

                const confirmResponse = await this.$swal({
                    html: 'Are you sure you want to import this week to SWO?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Import',
                    confirmButtonColor: '#27AE60',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                })

                if (!confirmResponse || !confirmResponse.value) {
                    return
                }

                const response = await this.axios.post('/api/units/publish3/' + this.unit.id + '/' + this.publication_id)

                if(!response) return

            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to import week: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        filterUnitStandards(state_grade) {
            this.filtered_unit_standards = []
            _.each(this.standards, standard => {
                if(
                    state_grade
                    &&
                    state_grade.standards_source != 'none'
                    &&
                    (
                        (state_grade.standards_source == 'state' && state_grade.grade >= standard.low_grade && state_grade.grade <= standard.high_grade && state_grade.state == standard.state)
                        ||
                        (state_grade.standards_source == 'national' && state_grade.grade >= standard.low_grade && state_grade.grade <= standard.high_grade && standard.state == 'NA')
                    )
                    ) {
                    this.filtered_unit_standards.push(standard)
                }
                if(!state_grade) this.filtered_unit_standards.push(standard)
            })
            this.available_unit_standards = [];
            _.each(this.unit_group.filtered_standards, standard => {
                if(
                    state_grade
                    &&
                    state_grade.standards_source != 'none'
                    &&
                    (
                        (state_grade.standards_source == 'state' && state_grade.grade >= standard.low_grade && state_grade.grade <= standard.high_grade && state_grade.state == standard.state)
                        ||
                        (state_grade.standards_source == 'national' && state_grade.grade >= standard.low_grade && state_grade.grade <= standard.high_grade && standard.state == 'NA')
                    )
                    ) {
                    this.available_unit_standards.push(standard)
                }
                if(!state_grade) this.available_unit_standards.push(standard)
            });


        },
        filterUnitGroupStandards(state_grade) {
            this.filtered_unit_group_standards = []
            _.each(this.unit_group.standards, standard => {
                if(
                    state_grade
                    &&
                    state_grade.standards_source != 'none'
                    &&
                    (
                        (
                            //state & grades match
                            state_grade.grade >= standard.low_grade
                            &&
                            state_grade.grade <= standard.high_grade
                            && state_grade.state == standard.state
                        )
                        ||
                        (
                            // nat standards && state & grade match
                            state_grade.standards_source == 'national'
                            &&
                            (standard.state == 'NA' || standard.state == 'na')
                            &&
                            state_grade.grade >= standard.low_grade
                            &&
                            state_grade.grade <= standard.high_grade
                        )
                    )
                ) {
                    this.filtered_unit_group_standards.push(standard)
                }
                if(!state_grade) this.filtered_unit_group_standards.push(standard)
            })
            this.$set(this.unit_group, 'filtered_standards', this.filtered_unit_group_standards)
        },
        getUnit () {
            this.$set(this.unit, 'loading', true)

            // let visible = null
            //
            // if (this.unit.visible) {
            //     visible = this.unit.visible
            // }

            this.$store.dispatch('getUnits', {'publication_id': this.publication_id, 'unit_id': this.unit.id})
            .then(response => {
                this.unit_data = response.data[0]
                this.$set(this.unit, 'loading', false)
            })
            .catch(error => {
                if (error) {
                    if (error.response && error.response.status && error.response.status == 401) {
                        this.$set(this.unit, 'visible', false)
                    }
                    this.$set(this.unit, 'loading', false)
                }
            })
        },
        getQuestions () {
            return new Promise((resolve, reject) => {
                this.axios.get('/api/units/questions/' + this.unit.id)
                .then(response => {
                    let questions = []
                    if (response.data) {
                        questions = response.data
                        questions = _.orderBy(questions, 'order_number')
                    }
                    // this.questions = questions
                    this.$set(this.unit, 'questions', questions)
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
            })
        },
        getArticles () {
            return new Promise((resolve, reject) => {
                this.axios.get('/api/units/articles/' + this.unit.id)
                .then(response => {
                    let articles = []
                    if (response.data) {
                        articles = response.data
                        articles = _.orderBy(articles, 'order_number')
                        articles = _.orderBy(articles, 'page')
                        articles = _.uniqBy(articles, 'unit_page_slot_id')
                    }
                    this.$set(this.unit, 'articles', articles)
                    resolve(response)
                })
                .catch(error => {
                    reject(error)
                })
            })
        },
        updateUnit () {
            this.axios.post('/api/units/update/' + this.unit.id, this.unit)
            .then(response => {
                this.unit_cache = _.cloneDeep(response.data)
            })
            .catch(error => {
                if (error) {
                    _.assign(this.unit, this.unit_cache)
                }
            })
        },
        updateUnitByProperty (key, value) {
            return new Promise((resolve, reject) => {
                this.$Progress.start()

                this.axios.post('/api/units/update/' + this.unit.id, {
                    [key]: value
                })
                .then(response => {
                    this.unit_cache = _.cloneDeep(response.data)
                    this.$Progress.finish()
                    resolve(response)
                })
                .catch(error => {
                    if (error) {
                        _.assign(this.unit, this.unit_cache)
                        this.$Progress.fail()
                    }
                    reject(error)
                })
            })
        },
        updateUnitTitle (title) {
            this.updateUnitByProperty('title', title)
            .then(() => {
                this.$set(this.unit, 'title', title)
            })
        },
        toggleStudentEditionLock () {
            if (!this.unit.se_locked) {
                this.$set(this.unit, 'se_locked', true)
                this.updateUnitByProperty('se_locked', true)
            } else {
                this.$set(this.unit, 'se_locked', false)
                this.updateUnitByProperty('se_locked', false)
            }
        },
        toggleUnitActivity () {
            if (!this.unit.is_activity) {
                this.$set(this.unit, 'is_activity', true)
                this.updateUnitByProperty('is_activity', true)
            } else {
                this.$set(this.unit, 'is_activity', false)
                this.updateUnitByProperty('is_activity', false)
            }
        },
        toggleSampleWeek () {
            if (!this.unit.is_sample) {
                this.$set(this.unit, 'is_sample', true)
                this.updateUnitByProperty('is_sample', true)
            } else {
                this.$set(this.unit, 'is_sample', false)
                this.updateUnitByProperty('is_sample', false)
            }
        },
        toggleOnlineOnly () {
            if (!this.unit.online_only) {
                this.$set(this.unit, 'online_only', true)
                this.updateUnitByProperty('online_only', true)
            } else {
                this.$set(this.unit, 'online_only', false)
                this.updateUnitByProperty('online_only', false)
            }
        },
        toggleCustomNumbering(){
            if(this.unit.custom_article_numbering == 1){
                this.unit.custom_article_numbering = 0;
            }
            else{
                this.unit.custom_article_numbering = 1;
            }
            this.axios.post('/api/units/update_custom_numbering/' + this.unit.id +'/'+this.unit.custom_article_numbering, {}, {responseType: 'blob'})
            .then(response => {
                this.getArticles();
            })
        },
        moveUnit () {
            this.show_move_unit_modal = true
        },
        startUnitDuplication () {
            this.show_duplicate_unit_modal = true
        },
        deleteUnit (unit, unit_group) {
            let message = null

            if (unit.title) {
                message = 'Are you sure you want to delete the following week? <div class="mt-3 text-left pl-3 border-left"><div>Week: <strong>' + unit.order_number + '</strong><div>Title: <strong>' + unit.title + '</strong></div></div>'
            } else {
                message = 'Are you sure you want to delete the following week? <div class="mt-3 text-left pl-3 border-left"><div>Week: <strong>' + unit.order_number + '</strong><div>Title: <span class="text-danger">no title</span></div></div>'
            }

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Delete Week',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.$set(unit, 'deleting', true)

                    this.axios.post('/api/units/delete/' + unit.id, {
                        unit_group_id: unit_group.id
                    })
                    .then(() => {
                        if (localStorage.getItem('Unit' + this.unit.id)) {
                            localStorage.removeItem('Unit' + this.unit.id)
                        }

                        this.$set(unit, 'deleting', false)

                        this.$store.dispatch('getUnitGroups', this.publication_id)
                        .then(response => {
                            // API returns publications; each has nested unit_groups (see store/unit_groups.js).
                            let refreshed_unit_group = null

                            if (response.data) {
                                _.forEach(response.data, publication => {
                                    const found = _.find(publication.unit_groups || [], { id: this.unit_group.id })
                                    if (found) {
                                        refreshed_unit_group = found
                                    }
                                })
                            }

                            if (
                                refreshed_unit_group &&
                                refreshed_unit_group.units &&
                                refreshed_unit_group.units.length
                            ) {
                                let nextAvailableUnit = _.last(refreshed_unit_group.units)

                                if (nextAvailableUnit) {
                                    // if the group the unit that was deleted from has other units, scroll to the next available unit
                                    // VueScrollTo.scrollTo("#unit" + nextAvailableUnit.id, 0, {
                                    //     offset: -144
                                    // })
                                }
                            }
                        })
                    })
                    .catch(() => {
                        this.$set(unit, 'deleting', false)
                    })
                }
            })
        },
        reorderUnits (unit, event) {
            //units/simple_reorder
            this.axios.post('/api/units/simple_reorder', {
                publication_id: this.unit_group.publication_id,
                unit_id: this.unit.id,
                old_order_number: unit.order_number,
                new_order_number: event.target.value
            })
            .then(() => {
                this.$store.dispatch('getUnitGroups', this.publication_id)
            })
        },

        definitionLinked (linked) {
            let is_linked = false

            if (linked == 1) {
                is_linked = true
            } else {
                is_linked = false
            }

            return is_linked
        },
        newQuestion () {
            this.show_question_modal = true
        },
        createQuestion (question) {
            this.axios.post('/api/units/create_question', {
                unit_id: this.unit.id,
                type: question.type,
                content: question.content,
                print_view: question.print_view,
                standards: question.standards,
                questions: question.questions ? question.questions : []
            })
            .then(() => {
                this.getQuestions()

                this.show_question_modal = false
            })
        },
        uploadWeeklyAssessment (file) {

            let form_data = new FormData()

            if (file) {
                form_data.append('assessment', file)
                console.log(form_data)
                this.uploading_assessment = true
                this.axios.post('/api/units/add_assessment_pdf/' + this.unit.id, form_data)
                .then(response => {
                    if (
                        response.data &&
                        response.data.assessment_url
                    ) {
                        this.unit_data.assessment_url = response.data.assessment_url
                        this.unit_data.assessment_title = response.data.assessment_title
                    }

                    this.uploading_assessment = false
                    this.assessment_file = null
                })
                .catch(error => {
                    if (error) {
                        this.uploading_assessment = false
                        this.assessment_file = null
                    }
                })
            }
        },
        removeWeeklyAssessment () {
            let message = null

            if (this.unit.title) {
                message = 'Are you sure you want to remove the Assessment for <strong>' + this.unit.title + '</strong>?'
            } else {
                message = 'Are you sure you want to remove this weekly Assessment?'
            }

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Assessment',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/units/remove_assessment_pdf/' + this.unit.id)
                    .then(() => {
                        this.unit_data.assessment_url = null
                        this.unit_data.assessment_title = null
                    })
                    .catch(() => {

                    })
                }
            })
        },

        addStandard (standard) {
            this.axios.post('/api/units/add_standard/' + this.unit.id, {
                standard_id: standard.guid
            })
            .then(() => {
                this.unit.standards.push(standard)
                this.filterUnitStandards(this.state_grade)

                if (!_.find(this.unit_group.standards, {guid: standard.guid})) {
                    this.axios.post('/api/unit_groups/add_standard/' + this.unit_group.id, {
                        standard_id: standard.guid
                    })
                    .then(() => {
                        this.$store.dispatch('getUnitGroups', this.publication_id)
                        .then(() => {
                            this.filterUnitGroupStandards(this.state_grade)
                        })
                    })
                }
            })
        },
        removeStandard (standard) {
            this.axios.post('/api/units/remove_standard/' + this.unit.id, {
                standard_id: standard.guid
            })
            .then(() => {
                let index = _.findIndex(this.unit.standards, {guid: standard.guid})
                this.unit.standards.splice(index, 1)
                this.filterUnitStandards(this.state_grade)
            })
        },
        toggleVisibility (value) {
            if (this.unit[value]) {
                this.$set(this.unit, value, false)
            } else {
                this.$set(this.unit, value, true)
            }
        },
        articleWordCount (string) {
            string = string.replace(/<[^>]*>?/gm, '')

            return string.split(' ').filter((n) => { return n != '' }).length
        },
        setContent (field, value) {
            this.$set(this.unit, field, value)
        },
        exportArticles(unit) {
            this.axios.get('/api/units/export_articles_to_text/' + unit.id, {responseType: 'blob'})
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', unit.title + '.txt');
                document.body.appendChild(link);
                link.click();
            })
        },
        setUnitArticlesAsComplete(unit){
            this.axios.post('/api/units/set_unit_articles_as_complete/' + unit.id, {}, {responseType: 'blob'})
            .then(response => {
                this.getArticles();
            })
        },
        setUnitArticlesAsPrintable(unit){
            this.axios.post('/api/units/set_unit_articles_as_printable/' + unit.id, {}, {responseType: 'blob'})
            .then(response => {
                this.getArticles();
            })
        },
        setUnitArticlesAsNotPrintable(unit){
            this.axios.post('/api/units/set_unit_articles_as_not_printable/' + unit.id, {}, {responseType: 'blob'})
            .then(response => {
                this.getArticles();
            })
        },
        openUploadTeacherEdition() {
            this.show_upload_teacher_edition_modal = true;
        },
        getTeacherEditionData(){
            if(this.unit.id && this.unit_group.id){

                this.axios.get('/api/units/get_teacher_edition_data/' + this.unit.id + '/' + this.unit_group.id)
                .then(response => {
                    this.teacher_edition_data = response.data[0];
                })
            }
        },
        async getBibliographyData(){
            try {
                const response = await this.axios.get('/api/units/get_bibliography_data/' + this.publication.id + '/' + this.unit.id)
                if (response.data) {
                    const returned_bibliography = (response.data && response.data.bibliography != null) ? response.data.bibliography : response.data;
                    this.bibliography = Array.isArray(returned_bibliography) ? returned_bibliography : Object.values(returned_bibliography || {});                    
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to get Bibliography data: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } 
        },
        hasLinkedArticlePedigrees(article) {
            if (!article || !Array.isArray(article.pedigrees)) {
                return false
            }

            return article.pedigrees.some((pedigree) => {
                return pedigree.relationship_type === 'Linked Article' && pedigree.unit_id !== this.unit.id
            })
        },
        async convertLinkedArticlesToDuplicates() {
            const linkedArticles = this.linkedArticles

            if (!linkedArticles.length) {
                return
            }

            const articleCount = linkedArticles.length
            const message = `
                Are you sure you want to convert ${articleCount} linked article${articleCount === 1 ? '' : 's'} to duplicate${articleCount === 1 ? '' : 's'}?
                <div class="alert alert-danger mt-2 mx-1 px-1">
                    <p>This will change the Article IDs and Question IDs.</p>
                    <strong>Student progress may be lost mid-year!</strong>
                </div>
            `

            const result = await this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Convert All',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E67E22',
                reverseButtons: true,
                customClass: 'swal-wider',
            })

            if (!result.value) {
                return
            }

            this.converting_linked_articles = true

            try {
                const response = await this.axios.post('/api/articles/linked-duplicate/batch', {
                    article_ids: linkedArticles.map((article) => article.article_id),
                    unit_id: this.unit.id,
                    publication_id: this.publication.id,
                })

                await this.getArticles()

                const convertedCount = response.data && response.data.converted_count ? response.data.converted_count : 0
                const failedCount = response.data && response.data.failed_count ? response.data.failed_count : 0

                if (failedCount) {
                    const failedArticleIds = response.data.failed.map((failure) => failure.article_id).join(', ')
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Converted ${convertedCount} linked article${convertedCount === 1 ? '' : 's'}, but ${failedCount} failed. Article IDs: ${failedArticleIds}`,
                        },
                        time: moment().toISOString(),
                    })
                } else {
                    await this.$swal('Conversion Complete', `Converted ${convertedCount} linked article${convertedCount === 1 ? '' : 's'} to duplicate${convertedCount === 1 ? '' : 's'}.`, 'success')
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to convert linked articles: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.converting_linked_articles = false
            }
        },
        updateTe(){
            this.axios.post('/api/units/get_te/' + this.unit.id )
            .then(response => {
                if (response.data) {
                    console.log('response.data', response.data.lesson_plan_url);
                    this.unit.lesson_plan_title = response.data.lesson_plan_title;
                    this.unit.lesson_plan_url = response.data.lesson_plan_url;
                }
            })
        },
        deleteUnitTe(){
            this.axios.delete('/api/units/delete_teacher_edition/' + this.unit.id)
            .then(response => {
                if (response.data) {
                    this.unit.lesson_plan_title = response.data.lesson_plan_title;
                    this.unit.lesson_plan_url = response.data.lesson_plan_url;
                }
            })
            .catch(error => {
                reject(error)
            })
        },
        compareToTrunk(unit_info){
            this.axios.get('/api/units/compare_trunk_unit/' + this.unit.id +'/'+ this.unit.trunks[0].trunk_unit_id)
            .then(response => {
                console.log('trunk_comparison', response.data);
                this.trunk_comparison = response.data;
                this.show_trunk_comparison_modal = true;
            })
        },
        updateToTrunk(){
            console.log('updateToTrunk');
        },
        refreshUnit(){
            this.$emit('refresh')
            this.show_trunk_comparison_modal = false;
        },
        copyToClipboard (unit_id) {
            const baseURL = window.location.origin + window.location.pathname;
            copy(`${baseURL}?unit_id=${unit_id}`, {
                asHtml: false
            })

            this.copying_to_clipboard = true

            setTimeout(() => {
                this.copying_to_clipboard = false
            }, 1500)
        },
    },
    created () {
        this.unit_cache = _.cloneDeep(this.unit)
        if(this.state_grade) this.filterUnitGroupStandards(this.state_grade)
        if(this.state_grade) this.filterUnitStandards(this.state_grade)
        // bad code, probably need to refactor to use vuex store to track articles_visible -KO
        eventBus.$emit('unit-created')
        eventBus.$on('articles-visible', (unit_id) => {
            if (this.unit.id == unit_id) {
                if(!this.unit.articles_visible) {
                    this.$set(this.unit, 'articles_visible', true)
                }
            }
        })
    },
}
</script>
